import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Copy, Trash2, Save, Loader2, Star } from "lucide-react";
import LetterBodyEditor from "@/components/LetterBodyEditor";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };

/** Settings → Letters & emails: clinic-editable letter templates and letter defaults. */
export default function LetterSettings() {
  const [templates, setTemplates] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [defaults, setDefaults] = useState({ letter_signoff: "", letter_footer: "" });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const load = async () => {
    try {
      const [{ data }, { data: settings }] = await Promise.all([
        api.get("/letter-templates"),
        api.get("/letter-settings"),
      ]);
      setTemplates(data.templates || []);
      setPlaceholders(data.placeholders || []);
      setDefaults({
        letter_signoff: settings.letter_signoff || "",
        letter_footer: settings.letter_footer || "",
      });
    } catch {
      setMessage("Letter settings could not be loaded. Please refresh the page.");
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    setMessage("");
    try {
      const payload = {
        name: editing.name,
        subject: editing.subject || "",
        body_html: editing.body_html || "",
        is_default: !!editing.is_default,
      };
      if (editing.id) await api.put(`/letter-templates/${editing.id}`, payload);
      else await api.post("/letter-templates", payload);
      setEditing(null);
      await load();
      setMessage("Template saved");
    } catch (err) {
      setMessage(err?.response?.data?.detail || "That template could not be saved.");
    }
    setBusy(false);
  };

  const act = async (fn, note) => {
    setBusy(true);
    try { await fn(); await load(); setMessage(note); }
    catch { setMessage("That action could not be completed."); }
    setBusy(false);
  };

  const saveDefaults = () => act(() => api.put("/letter-settings", defaults), "Letter defaults saved");

  return (
    <div className="space-y-6" data-testid="letter-settings">
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base text-[#1A1B1A]" style={H}>Letter templates</h3>
            <p className="text-xs text-[#757675]">Used by New letter in a patient's Letters & emails</p>
          </div>
          <button
            onClick={() => setEditing({ name: "New template", subject: "", body_html: "<p>Dear </p><p><br></p>", is_default: false })}
            data-testid="new-letter-template-btn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#205B5A] text-white text-xs font-medium hover:bg-[#174544]"
          >
            <Plus className="w-3.5 h-3.5" /> New template
          </button>
        </div>

        {message && <p className="text-[10px] text-[#205B5A] mb-2" data-testid="letter-settings-message">{message}</p>}

        <div className="space-y-2" data-testid="letter-template-list">
          {templates.map((t) => (
            <div key={t.id} className="flex items-center gap-3 bg-white border border-[#1A1B1A]/[0.06] rounded-xl px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[#1A1B1A] flex items-center gap-1.5">
                  {t.name}
                  {t.is_default && <Star className="w-3 h-3 text-[#205B5A]" />}
                </p>
                <p className="text-[10px] text-[#757675] truncate">{t.subject || "No subject"}</p>
              </div>
              <button onClick={() => setEditing(t)} data-testid={`edit-letter-template-${t.id}`}
                className="text-[10px] font-medium text-[#205B5A] px-2 py-1 rounded-lg hover:bg-[#205B5A]/5">Edit</button>
              <button onClick={() => act(() => api.post(`/letter-templates/${t.id}/duplicate`), "Template duplicated")}
                data-testid={`duplicate-letter-template-${t.id}`} title="Duplicate"
                className="p-1.5 rounded-lg text-[#4A4B4A] hover:bg-[#FAF9F6]"><Copy className="w-3.5 h-3.5" /></button>
              <button onClick={() => act(() => api.delete(`/letter-templates/${t.id}`), "Template archived")}
                data-testid={`delete-letter-template-${t.id}`} title="Archive"
                className="p-1.5 rounded-lg text-red-400 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          {templates.length === 0 && <p className="text-xs text-[#757675]">No templates yet.</p>}
        </div>
      </div>

      {editing && (
        <div className="bg-white border border-[#1A1B1A]/[0.06] rounded-2xl p-4 space-y-3" data-testid="letter-template-editor">
          <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
            placeholder="Template name" data-testid="letter-template-name-input"
            className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm" />
          <input value={editing.subject || ""} onChange={(e) => setEditing({ ...editing, subject: e.target.value })}
            placeholder="Subject, e.g. Referral for {patient_name}" data-testid="letter-template-subject-input"
            className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm" />
          <div className="flex flex-wrap gap-1.5">
            {placeholders.map((p) => (
              <button key={p} type="button" data-testid={`subject-placeholder-${p.replace(/[{}]/g, "")}`}
                onClick={() => setEditing((e) => ({ ...e, subject: `${e.subject || ""}${p}` }))}
                className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-[#205B5A]/[0.06] text-[#205B5A] hover:bg-[#205B5A]/[0.12]">
                {p}
              </button>
            ))}
          </div>
          <LetterBodyEditor
            value={editing.body_html || ""}
            onChange={(html) => setEditing((e) => ({ ...e, body_html: html }))}
            placeholders={placeholders}
            editorKey={editing.id || "new-template"}
            testid="letter-template-body-input"
          />
          <p className="text-[10px] text-[#757675]">
            Placeholders are replaced with the patient's details when a letter is written.
          </p>
          <label className="flex items-center gap-2 text-xs text-[#4A4B4A]">
            <input type="checkbox" checked={!!editing.is_default} data-testid="letter-template-default-checkbox"
              onChange={(e) => setEditing({ ...editing, is_default: e.target.checked })} />
            Use as the default template
          </label>
          <div className="flex gap-2">
            <button onClick={save} disabled={busy} data-testid="save-letter-template-btn"
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#205B5A] text-white text-xs font-medium hover:bg-[#174544] disabled:opacity-50">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save template
            </button>
            <button onClick={() => setEditing(null)} className="px-3 py-2 rounded-xl border border-[#1A1B1A]/10 text-xs">Cancel</button>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#1A1B1A]/[0.06] rounded-2xl p-4 space-y-3">
        <h3 className="text-base text-[#1A1B1A]" style={H}>Letter defaults</h3>
        <div>
          <label className="text-[10px] font-medium text-[#757675] uppercase tracking-wider">Default sign-off</label>
          <input value={defaults.letter_signoff} onChange={(e) => setDefaults({ ...defaults, letter_signoff: e.target.value })}
            placeholder="Kind regards" data-testid="letter-signoff-input"
            className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm" />
        </div>
        <div>
          <label className="text-[10px] font-medium text-[#757675] uppercase tracking-wider">Letter footer</label>
          <input value={defaults.letter_footer} onChange={(e) => setDefaults({ ...defaults, letter_footer: e.target.value })}
            placeholder="Clinic registration or contact line" data-testid="letter-footer-input"
            className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm" />
        </div>
        <p className="text-[10px] text-[#757675]">
          Your clinic name, address and contact details come from Settings → Clinic and appear on every letterhead.
        </p>
        <button onClick={saveDefaults} disabled={busy} data-testid="save-letter-defaults-btn"
          className="px-3 py-2 rounded-xl border border-[#1A1B1A]/10 text-xs text-[#1A1B1A] hover:bg-[#FAF9F6] disabled:opacity-50">
          Save defaults
        </button>
      </div>
    </div>
  );
}
